import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Chip,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Alert,
  Badge,
  Divider,
  useTheme,
  alpha,
  Button,
} from '@mui/material';
import {
  Person as PersonIcon,
  Computer as ComputerIcon,
  Phone as PhoneIcon,
  Tablet as TabletIcon,
  AccessTime as AccessTimeIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  Circle as CircleIcon,
  SignalCellular4Bar as SignalIcon,
  SignalCellular0Bar as OfflineIcon,
  ClearAll as CleanIcon,
  Close as CloseIcon,
  Favorite as HeartIcon,
  Visibility as EyeIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { toast } from 'react-toastify';
import UserActionMenu from '../components/UserActionMenu';

// Animated Heartbeat Component
const AnimatedHeartbeat = ({ isActive = true }) => {
  const [scale, setScale] = React.useState(1);

  React.useEffect(() => {
    if (!isActive) return;

    const interval = setInterval(() => {
      setScale(1.2);
      setTimeout(() => setScale(1), 200);
    }, 1000);

    return () => clearInterval(interval);
  }, [isActive]);

  return (
    <HeartIcon
      sx={{
        fontSize: 16,
        color: isActive ? '#ef4444' : '#9ca3af',
        transform: `scale(${scale})`,
        transition: 'transform 0.2s ease-in-out',
        animation: isActive ? 'heartbeat 1s ease-in-out infinite' : 'none',
        '@keyframes heartbeat': {
          '0%': { transform: 'scale(1)' },
          '50%': { transform: 'scale(1.2)' },
          '100%': { transform: 'scale(1)' },
        },
      }}
    />
  );
};

const OnlineUsers = () => {
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserDetails, setShowUserDetails] = useState(false);
  const [cleaningUp, setCleaningUp] = useState(false);
  const theme = useTheme();
  const { user } = useAuth();

  const fetchOnlineUsers = async () => {
    try {
      setRefreshing(true);
      const response = await api.dashboard.getOnlineUsers();
      const users = Array.isArray(response) ? response : [];

      const transformedUsers = users.map(user => ({
        _id: user.userId,
        fullname: user.fullname,
        username: user.username,
        email: user.email,
        lastActivity: user.lastActivity,
        lastHeartbeat: user.lastHeartbeat,
        onlineStatus: user.onlineStatus,
        lastActivityFormatted: user.lastActivityFormatted,
        deviceCount: user.deviceCount || 0,
        deviceInfo: user.deviceInfo || [],
        status: user.onlineStatus === 'online' ? 'active' : 'inactive'
      }));

      setOnlineUsers(transformedUsers);
      setError('');
    } catch (err) {
      setError(err.message || 'Error fetching online users');
      toast.error('Failed to fetch online users');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchOnlineUsers();
    const interval = setInterval(fetchOnlineUsers, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleRefresh = () => {
    fetchOnlineUsers();
  };

  const handleCleanupOfflineUsers = async () => {
    try {
      setCleaningUp(true);
      await api.dashboard.cleanupOfflineUsers();
      toast.success('Offline users cleaned up successfully');
      fetchOnlineUsers();
    } catch (error) {
      toast.error('Failed to cleanup offline users');
    } finally {
      setCleaningUp(false);
    }
  };

  const handleUserClick = (user) => {
    setSelectedUser(user);
    setShowUserDetails(true);
  };

  const getDeviceIcon = (deviceType) => {
    switch (deviceType) {
      case 'mobile':
        return <PhoneIcon fontSize="small" />;
      case 'tablet':
        return <TabletIcon fontSize="small" />;
      case 'desktop':
        return <ComputerIcon fontSize="small" />;
      default:
        return <ComputerIcon fontSize="small" />;
    }
  };

  const getTimeAgo = (timestamp) => {
    if (!timestamp) return 'Never';
    const now = new Date();
    const time = new Date(timestamp);
    const diffInSeconds = Math.floor((now - time) / 1000);

    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    return `${Math.floor(diffInSeconds / 86400)}d ago`;
  };

  const getStatusColor = (status) => {
    return status === 'online' || status === 'active' ? '#22c55e' : '#ef4444';
  };

  const getDeviceTypeColor = (deviceType) => {
    switch (deviceType) {
      case 'mobile':
        return theme.palette.primary.main;
      case 'tablet':
        return theme.palette.secondary.main;
      case 'desktop':
        return theme.palette.success.main;
      default:
        return theme.palette.grey[500];
    }
  };

  const getStatusIcon = (status) => {
    return status === 'online' || status === 'active'
      ? <SignalIcon sx={{ color: '#22c55e' }} />
      : <OfflineIcon sx={{ color: '#ef4444' }} />;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
          Online Users
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Chip
            icon={<CircleIcon sx={{ color: '#22c55e' }} />}
            label={`${onlineUsers.length} Online`}
            color="success"
            variant="outlined"
          />
          <Tooltip title="Refresh">
            <IconButton
              onClick={handleRefresh}
              disabled={refreshing}
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                '&:hover': {
                  bgcolor: alpha(theme.palette.primary.main, 0.2),
                },
              }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Cleanup Offline Users">
            <IconButton
              onClick={handleCleanupOfflineUsers}
              disabled={cleaningUp}
              sx={{
                bgcolor: alpha(theme.palette.warning.main, 0.1),
                '&:hover': {
                  bgcolor: alpha(theme.palette.warning.main, 0.2),
                },
              }}
            >
              <CleanIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper elevation={2} sx={{ borderRadius: 3, overflow: 'hidden' }}>
            <Box sx={{ p: 2, bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Currently Online ({onlineUsers.length})
              </Typography>
            </Box>

            {onlineUsers.length === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <PersonIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  No users currently online
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Users will appear here when they are actively using the platform
                </Typography>
              </Box>
            ) : (
              <List sx={{ p: 0 }}>
                {onlineUsers.map((user, index) => (
                  <React.Fragment key={user._id || user.id}>
                    <ListItem
                      sx={{
                        p: 2,
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          bgcolor: alpha(theme.palette.primary.main, 0.05),
                        },
                      }}
                      onClick={() => handleUserClick(user)}
                    >
                      <ListItemAvatar>
                        <Badge
                          overlap="circular"
                          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                          badgeContent={getStatusIcon(user.onlineStatus)}
                        >
                          <Avatar sx={{ width: 48, height: 48, bgcolor: alpha(theme.palette.primary.main, 0.2) }}>
                            {(user.fullname || user.username || user.email || 'U').charAt(0).toUpperCase()}
                          </Avatar>
                        </Badge>
                      </ListItemAvatar>

                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              {user.fullname || user.username || 'Unknown User'}
                            </Typography>
                            <Chip
                              label={user.onlineStatus || 'Online'}
                              size="small"
                              sx={{
                                bgcolor: getStatusColor(user.onlineStatus),
                                color: 'white',
                                fontSize: '0.7rem',
                                height: 20,
                              }}
                            />
                          </Box>
                        }
                        secondary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <AccessTimeIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                              <Typography variant="body2" color="text.secondary">
                                Last seen: {user.lastActivityFormatted || getTimeAgo(user.lastActivity)}
                              </Typography>
                            </Box>
                            {user.lastHeartbeat && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <AnimatedHeartbeat isActive={user.onlineStatus === 'online'} />
                                <Typography variant="body2" color="text.secondary">
                                  Last heartbeat: {getTimeAgo(user.lastHeartbeat)}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        }
                      />

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {user.deviceInfo?.map((device, deviceIndex) => (
                          <Tooltip
                            key={deviceIndex}
                            title={`${device.deviceType || 'Unknown'} - ${device.platform || 'Unknown platform'} - ${device.os || 'Unknown OS'}`}
                          >
                            <Box
                              sx={{
                                p: 0.5,
                                borderRadius: 1,
                                bgcolor: alpha(getDeviceTypeColor(device.deviceType), 0.1),
                                color: getDeviceTypeColor(device.deviceType),
                              }}
                            >
                              {getDeviceIcon(device.deviceType)}
                            </Box>
                          </Tooltip>
                        ))}
                        <Tooltip title="View Details">
                          <IconButton size="small" onClick={() => handleUserClick(user)}>
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <UserActionMenu user={user} />
                      </Box>
                    </ListItem>
                    {index < onlineUsers.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card elevation={2} sx={{ borderRadius: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                Online Statistics
              </Typography>
              {/* Stats content (unchanged) */}
              {/* ... */}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* ✅ Fixed: Modal only shows when showUserDetails is true */}
      {selectedUser && showUserDetails && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1300,
          }}
          onClick={() => setShowUserDetails(false)}
        >
          <Card
            sx={{
              maxWidth: 500,
              width: '90%',
              maxHeight: '80vh',
              overflow: 'auto',
              position: 'relative',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <CardContent>
              <IconButton
                onClick={() => setShowUserDetails(false)}
                sx={{ position: 'absolute', top: 8, right: 8 }}
              >
                <CloseIcon />
              </IconButton>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Avatar sx={{ width: 64, height: 64, bgcolor: alpha(theme.palette.primary.main, 0.2) }}>
                  {(selectedUser.fullname || selectedUser.username || selectedUser.email || 'U').charAt(0).toUpperCase()}
                </Avatar>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {selectedUser.fullname || selectedUser.username || 'Unknown User'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedUser.email}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Username
                  </Typography>
                  <Typography variant="body1">
                    {selectedUser.username || 'Not provided'}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Status
                  </Typography>
                  <Chip
                    label={selectedUser.onlineStatus || 'Unknown'}
                    size="small"
                    sx={{
                      bgcolor: getStatusColor(selectedUser.onlineStatus),
                      color: 'white',
                    }}
                  />
                </Box>

                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Last Seen
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <EyeIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                    <Typography variant="body1">
                      {selectedUser.lastActivityFormatted || getTimeAgo(selectedUser.lastActivity)}
                    </Typography>
                  </Box>
                </Box>

                {selectedUser.lastHeartbeat && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Last Heartbeat
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AnimatedHeartbeat isActive={selectedUser.onlineStatus === 'online'} />
                      <Typography variant="body1">
                        {getTimeAgo(selectedUser.lastHeartbeat)}
                      </Typography>
                    </Box>
                  </Box>
                )}

                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Connected Devices ({selectedUser.deviceCount || 1})
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
                    {selectedUser.deviceInfo?.map((device, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          p: 1,
                          borderRadius: 1,
                          bgcolor: alpha(theme.palette.grey[100], 0.5),
                        }}
                      >
                        {getDeviceIcon(device.deviceType)}
                        <Typography variant="body2">
                          {device.deviceType || 'Unknown'} - {device.platform || 'Unknown platform'} - {device.os || 'Unknown OS'}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
      )}
    </Box>
  );
};

export default OnlineUsers;
